import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import styles from './profile.module.css'; 
import { auth, db } from '../../firebase/firebase'; 
import { collection, query, where, getDocs } from 'firebase/firestore';
import banner from '../../assets/img/Rectangle.png';

import HeaderBlack from '../../components/header/headerBlack/headerBlack';
import StatusCard from '../../components/statusCard/statusCard';
import PatientsTable from '../../components/patientsTable/patientsTable';
import usePatientSearch from '../../hooks/usePatientSearch';
import SearchInput from '../../components/search/SearchInput';


const AnonymousIcon = () => (
    <svg 
        viewBox="0 0 24 24" 
        style={{
            width: '100%',
            height: '100%',
            color: '#718096',
            backgroundColor: '#E2E8F0'
        }}
        fill="currentColor"
    >
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
);

const Profile = () => {
  const [userName, setUserName] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  // Remove filteredPatients state, use hook instead
  const { searchTerm, setSearchTerm, filteredPatients } = usePatientSearch(
    patients.filter(patient => {
      if (activeTab === 'all') return patient.archived !== 1;
      if (activeTab === 'actions') return [0, 2, 3].includes(patient.status) && patient.archived !== 1;
      if (activeTab === 'archived') return patient.archived === 1;
      return true;
    }),
    ['name', 'surname']
  );
  const navigate = useNavigate();

  // Fetch user data using Firestore query
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const user = auth.currentUser;
        
        if (user) {
          /*console.log("Current user:", user);*/
          // Get the user's email and convert to lowercase for comparison
          const userEmail = user.email.toLowerCase();
          
          // Try to find the user with exact email match first
          const medecinsRef = collection(db, 'medecins'); 
          const q = query(medecinsRef, where('email', '==', user.email)); 
          let querySnapshot = await getDocs(q);
          
          // If no match, try with lowercase email
          if (querySnapshot.empty) {
            /*console.log("Trying case-insensitive email match");*/
            // Get all medecins and filter manually since Firestore doesn't support case-insensitive queries
            const allMedecinsQuery = query(medecinsRef);
            const allMedecinsSnapshot = await getDocs(allMedecinsQuery);
            
            // Find a document with case-insensitive email match
            const matchingDocs = allMedecinsSnapshot.docs.filter(doc => {
              const docEmail = doc.data().email;
              return docEmail && docEmail.toLowerCase() === userEmail;
            });
            
            if (matchingDocs.length > 0) {
              const userData = matchingDocs[0].data();
              /*console.log("Found user with case-insensitive email match:", userData);*/
              setUserName(userData.name || '');
            } else {
              /*console.log("User not found in medecins collection even with case-insensitive match");*/
              setUserName(user.displayName || 'Utilisateur');
            }
          } else {
            const userData = querySnapshot.docs[0].data();
            /*console.log("User data retrieved:", userData);*/
            setUserName(userData.name || '');
          }
        } else {
          /*console.log("No current user found");*/
        }
      } catch (error) {
        /*console.error('Error fetching user data:', error);*/
      } finally {
        setLoading(false);
      }
    };

    const authListener = auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        fetchUserData();
      } else {
        setLoading(false);
        navigate('/login');
      }
    });
    
    return () => authListener();
  }, [navigate]);

  // Fetch patients data
  useEffect(() => {
    const fetchPatients = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const patientsRef = collection(db, 'patients');
          const q = query(
            patientsRef,
            where('userId', '==', user.uid),
          );
          const querySnapshot = await getDocs(q);
          
          const patientsData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setPatients(patientsData);
        } catch (error) {
          /*console.error('Error fetching patients:', error);*/
        }
      } else {
        // Add listener for auth state changes
        const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
          if (authUser) {
            try {
              const patientsRef = collection(db, 'patients');
              const q = query(
                patientsRef,
                where('userId', '==', authUser.uid),
              );
              const querySnapshot = await getDocs(q);
              
              const patientsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              setPatients(patientsData);
            } catch (error) {
              /*console.error('Error fetching patients:', error);*/
            }
          }
        });
        
        // Clean up the listener
        return () => unsubscribe();
      }
    };

    fetchPatients();
  }, []);

 
  // Handle patient selection
  const handlePatientClick = (patient) => {
    navigate(`/dossier`, { state: { patient } });
  };

  
  return (
    <div>
      <HeaderBlack/>
      <main>
        {loading ? (
          <div className={styles.loadingSpinner}>
            Loading...
          </div>
        ) : (
          <>
            
            <StatusCard/>
            <div className={styles.patientsList}>
              <div className={styles.headerContent}>
                <h1>Patients</h1>
                <button className={styles.addPatientBtn} onClick={() => navigate('/ajouterPatient')}>
                  <span>Ajouter un nouveau patient</span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </button>
              </div>

              <nav className={styles.tabs}>
                <button 
                  className={`${styles.tab} ${activeTab === 'all' ? styles.active : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  Tous les patients
                </button>
                <button 
                  className={`${styles.tab} ${activeTab === 'actions' ? styles.active : ''}`}
                  onClick={() => setActiveTab('actions')}
                >
                  Actions requises
                  <span className={styles.actionCount}>
                    {patients.filter(patient => 
                      [0, 2, 3].includes(patient.status) && patient.archived !== 1
                    ).length}
                  </span>
                </button>
                <button 
                  className={`${styles.tab} ${activeTab === 'archived' ? styles.active : ''}`}
                  onClick={() => setActiveTab('archived')}
                >
                  Archivés
                </button>
                <div className={styles.searchContainer}>
                  <svg className={styles.searchIcon} width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <SearchInput value={searchTerm} onChange={setSearchTerm} className={styles.searchInput} />
                </div>
              </nav>

              <PatientsTable 
                patients={filteredPatients} 
                onPatientClick={handlePatientClick}
                isActionsTab={activeTab === 'actions'}
              />

              <div className={styles.pagination}>
                <button className={styles.active}>1</button>
                <button>2</button>
                <button>3</button>
                <button>Suivant</button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Profile;
